import clsx from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import { MapPinIcon, ClockIcon, Battery50Icon, XMarkIcon } from '@heroicons/react/24/outline';
import type { Location } from '../../store/realtimeStore';
import PhaseChip from '../PhaseChip';
import { getPlatoon, ACTIVE_SOS_STATUSES } from './mapUtils';

export interface PlatoonsPanelProps {
  filteredLocations: Location[];
  platoonCounts: { onlineCount: number; offlineCount: number; total: number };
  selectedLocation: string | null;
  onSelectLocation: (deviceId: string) => void;
  sosEvents: any[];
  selectedPlatoonId: string | null;
  pathLoading: boolean;
  onShowPath: (platoonId: string) => void;
  onClearPath: () => void;
  /** Compact header for the mobile bottom sheet (sheet supplies its own title). */
  hideHeader?: boolean;
}

/**
 * Tracked-platoons list. Lives in the desktop right column and the mobile
 * "Platoons" bottom sheet.
 */
export default function PlatoonsPanel({
  filteredLocations,
  platoonCounts,
  selectedLocation,
  onSelectLocation,
  sosEvents,
  selectedPlatoonId,
  pathLoading,
  onShowPath,
  onClearPath,
  hideHeader,
}: PlatoonsPanelProps) {
  return (
    <div className="flex flex-col h-full min-h-0">
      {!hideHeader && (
        <div className="p-4 border-b border-gray-700 flex-shrink-0">
          <h2 className="text-lg font-semibold text-white">Platoons</h2>
          <p className="text-sm text-gray-400">{filteredLocations.length} tracked</p>
          <p className="text-xs text-gray-500 mt-1">
            {platoonCounts.onlineCount} online, {platoonCounts.offlineCount} offline
          </p>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-0">
        {hideHeader && (
          <p className="px-1 text-xs text-gray-500">
            {filteredLocations.length} tracked · {platoonCounts.onlineCount} online, {platoonCounts.offlineCount} offline
          </p>
        )}
        {filteredLocations.map((loc) => {
          const platoon = getPlatoon(loc);
          const platoonId = loc.platoonId || platoon?.id;
          const isOnline = new Date(loc.serverTime).getTime() > Date.now() - 5 * 60 * 1000;
          const hasSOS =
            !!platoon?.sos ||
            sosEvents.some(
              (sos) =>
                !!platoonId &&
                sos.platoonId === platoonId &&
                !!sos.status &&
                ACTIVE_SOS_STATUSES.includes(sos.status)
            );
          const phase = platoon?.currentPhase;
          const callsign = platoon?.callsign || 'Unknown';

          return (
            <div
              key={loc.deviceId}
              className={clsx(
                'p-3 rounded-lg border transition-all',
                selectedLocation === loc.deviceId
                  ? 'bg-blue-600/20 border-blue-500'
                  : 'bg-gray-800/50 border-gray-700 hover:border-gray-600',
                hasSOS && 'border-red-500 bg-red-500/10'
              )}
            >
              <div onClick={() => onSelectLocation(loc.deviceId)} className="cursor-pointer touch-manipulation">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className={clsx(
                        'w-2 h-2 rounded-full',
                        hasSOS ? 'bg-red-500 animate-pulse' : isOnline ? 'bg-green-500' : 'bg-gray-500'
                      )}
                    />
                    <span className="font-semibold text-white text-sm">{callsign}</span>
                  </div>
                  {hasSOS && (
                    <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded animate-pulse">SOS</span>
                  )}
                </div>

                <div className="mt-1 flex items-center gap-2 text-xs">
                  {platoon?.platoonNumber != null && (
                    <span className="text-gray-400">Plt {platoon.platoonNumber}</span>
                  )}
                  {platoon?.unit?.code && <span className="text-gray-500">{platoon.unit.code}</span>}
                </div>

                {phase && (
                  <div className="mt-1">
                    <PhaseChip phase={phase} size="xs" />
                  </div>
                )}

                <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <Battery50Icon className="w-3 h-3" />
                    {loc.battery ?? '--'}%
                  </div>
                  <div className="flex items-center gap-1">
                    <ClockIcon className="w-3 h-3" />
                    {formatDistanceToNow(new Date(loc.serverTime), { addSuffix: true })}
                  </div>
                </div>
              </div>

              {/* Travel path toggle */}
              <div className="mt-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (platoonId) {
                      if (selectedPlatoonId === platoonId) onClearPath();
                      else onShowPath(platoonId);
                    }
                  }}
                  className={clsx(
                    'w-full py-1.5 px-2 min-h-11 lg:min-h-0 rounded text-xs font-semibold transition-colors flex items-center justify-center gap-1 touch-manipulation',
                    selectedPlatoonId === platoonId
                      ? 'bg-amber-500/20 text-amber-400 border border-amber-500/50 hover:bg-amber-500/30'
                      : 'bg-purple-500/20 text-purple-400 border border-purple-500/50 hover:bg-purple-500/30'
                  )}
                  disabled={!platoonId}
                >
                  {pathLoading && selectedPlatoonId === platoonId ? (
                    <>
                      <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Loading...
                    </>
                  ) : selectedPlatoonId === platoonId ? (
                    <>
                      <XMarkIcon className="w-3 h-3" />
                      Hide Travel Path
                    </>
                  ) : (
                    <>
                      <MapPinIcon className="w-3 h-3" />
                      Show Travel Path (24h)
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}

        {filteredLocations.length === 0 && (
          <div className="text-center py-8">
            <MapPinIcon className="w-12 h-12 text-gray-600 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">No platoons tracked</p>
            <p className="text-gray-600 text-xs mt-1">Enable the "Offline" filter to see all</p>
          </div>
        )}
      </div>
    </div>
  );
}
